import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import cn from "classnames";
import Link from "next/link";
import Image from "next/image";
import t from "../texts/t";
import { AppSettings } from "../lib/get-app-settings";
import styles from "../styles/layout-sidebar.light.module.css";
import searchStyles from "../styles/search.module.css";
import getBaseUrl from "../lib/get-base-url";
import usePrefersColorScheme from "@atlas/tool/box/hooks/usePrefersColorScheme";
import { HelpCenterTheme } from "../lib/load-company-settings";
import type { Section } from "../lib/load-categories";
import type { Article } from "../lib/load-article";
import ChevronIcon from "@atlas/uicorn/lib/Icon/ChevronIcon";
import { useRouter } from "next/router";
import CrossIcon from "@atlas/uicorn/lib/Icon/CrossIcon";
import window from "@atlas/tool/box/browser/window";
import useRefCallback from "use-ref-callback";
import IconButton from "@atlas/uicorn/lib/Control/IconButton";
import MenuBurgerIcon from "@atlas/uicorn/lib/Icon/MenuBurgerIcon";
import SearchModal from "./search-modal";
import MagnifyingGlassIcon from "@atlas/uicorn/lib/Icon/MagnifyingGlassIcon";
import hotkey from "@atlas/tool/box/event/hotkey";
import useRequest from "use-request";
import { buildBasePath } from "../lib/api";
import useUrlWithPreview from "../lib/use-url-with-preview";
import imageDomainsWhitelist from "../lib/image-domains-whitelist.json";
import loadCategories from "../lib/load-categories";
import loadAllArticles from "../lib/load-all-articles";

const customerPortalPath = "/customer-portal";

export default function LayoutSidebarLight(props: TLayoutSidebarLightProps) {
  const {
    className,
    hasTitle,
    appSettings,
    categoryName,
    searchValue,
    // categories,
    // articles,
    selectedArticleSlug,
    links,
  } = props;

  const baseUrl = useMemo(() => getBaseUrl(appSettings), [appSettings]);
  const getUrlWithPreview = useUrlWithPreview();

  const sidebarRequest = useRequest(
    () =>
      Promise.all([
        loadCategories(appSettings.slug, undefined, appSettings.appId).then(
          (categories: Section[] | undefined) =>
            categories?.sort((a, b) => a.order - b.order) || []
        ) as Promise<Section[]>,
        loadAllArticles(appSettings.slug, undefined, appSettings.appId).then(
          (articles: Article[] | undefined) =>
            articles?.sort((a, b) => a.order - b.order) || []
        ) as Promise<Article[]>,
      ]),
    []
  );

  const [categories, articles] = useMemo(() => {
    if (!sidebarRequest.value) return [[], []];
    return sidebarRequest.value;
  }, [sidebarRequest.value]);

  const prefersColorScheme = usePrefersColorScheme();

  const [logoUrl, setLogoUrl] = useState<string>();
  const logoContainerStyle = useMemo(
    () => (logoUrl && { backgroundImage: `url(${logoUrl})` }) || undefined,
    [logoUrl]
  );

  useEffect(
    () =>
      setLogoUrl(() => {
        if (appSettings.theme === HelpCenterTheme.LIGHT)
          return appSettings.logoUrl;
        if (appSettings.theme === HelpCenterTheme.DARK)
          return appSettings.logoDarkUrl;
        return prefersColorScheme === "light"
          ? appSettings.logoUrl
          : appSettings.logoDarkUrl;
      }),
    [
      prefersColorScheme,
      appSettings.theme,
      appSettings.logoUrl,
      appSettings.logoDarkUrl,
    ]
  );

  const router = useRouter();
  const isCustomerPortalPage = useMemo(
    () => router.pathname.includes(customerPortalPath),
    [router.pathname]
  );

  const selectedSlugs = useMemo(() => {
    if (!selectedArticleSlug) return [];

    const selectedArticle = articles.find(
      (a) => a.slug === selectedArticleSlug
    );
    if (!selectedArticle) return [selectedArticleSlug];

    return [
      selectedArticleSlug,
      ...collectParentCategorySlugs(categories, selectedArticle.categoryId),
    ];
  }, [selectedArticleSlug, categories, articles]);

  const [searchQuery, setSearchQuery] = useState(searchValue);
  useEffect(() => {
    if (!window) return;

    const url = new URL(window.location.href);
    if (!searchQuery) url.searchParams.delete("query");
    else url.searchParams.set("query", searchQuery);
    window.history.replaceState({}, "", url.href);
  }, [searchQuery]);

  const handleSearchInputChange = useRefCallback<
    React.ChangeEventHandler<HTMLInputElement>
  >((e) => setSearchQuery(e.target.value));

  const [mobileNavOpened, setMobileNavOpened] = useState(false);
  const toggleMobileNav = useRefCallback(() => setMobileNavOpened((v) => !v));
  const asideRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const parent = asideRef.current?.parentElement;
    if (parent) {
      parent.style.overflow = mobileNavOpened ? "hidden" : "auto";
    }
  }, [mobileNavOpened]);
  useEffect(() => setMobileNavOpened(false), [router]);

  const [searchOpened, setSearchOpened] = useState(false);
  const toggleSearch = useRefCallback(() => setSearchOpened((v) => !v));
  useEffect(() => {
    const callback = (event: KeyboardEvent) =>
      searchHotkey.matches(event) && toggleSearch();
    window?.addEventListener("keydown", callback);
    return () => window?.removeEventListener("keydown", callback);
  }, [toggleSearch]);

  const isLogoUrlWhitelisted = useMemo<boolean>(() => {
    if (!logoUrl) return false;
    try {
      const url = new URL(logoUrl);
      return imageDomainsWhitelist.includes(url.host);
    } catch (error) {
      console.error(error);
      return false;
    }
  }, [logoUrl]);

  return (
    <aside
      className={cn(styles.menu, className, {
        [styles.menuMobileOpened]: mobileNavOpened,
      })}
      ref={asideRef}
    >
      {(logoUrl || appSettings.title) && (
        <header className={styles.menuHeader}>
          <Link
            href={getUrlWithPreview(
              baseUrl.endsWith("/") ? baseUrl : baseUrl + "/"
            )}
            className={styles.menuTitleLink}
          >
            {logoUrl && (
              <span
                className={styles.menuTitleLinkLogoContainer}
                style={logoContainerStyle}
              >
                {isLogoUrlWhitelisted ? (
                  <Image
                    className={styles.menuLogo}
                    src={logoUrl}
                    alt={appSettings.name}
                    width={464}
                    height={80}
                  />
                ) : (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={logoUrl} alt={appSettings.name} />
                )}
              </span>
            )}
            {appSettings.title &&
              (hasTitle ? (
                <h1 className={styles.menuTitle}>{appSettings.title}</h1>
              ) : (
                <span className={styles.menuTitle}>{appSettings.title}</span>
              ))}
          </Link>
        </header>
      )}
      <button
        type="button"
        className={styles.menuSearch}
        onClick={toggleSearch}
      >
        <MagnifyingGlassIcon />
        <span className={styles.menuSearchLabel}>
          {t("Search for articles...", appSettings.language)}
        </span>
        <kbd className={searchStyles.menuSearchKey}>
          <big suppressHydrationWarning>
            {searchHotkey.toString().slice(0, -1)}
          </big>
          <span>{searchHotkey.toString().slice(-1)}</span>
        </kbd>
      </button>
      <SearchModal
        appSettings={appSettings}
        baseUrl={baseUrl}
        opened={searchOpened}
        onClose={toggleSearch}
      />
      {links ? (
        <menu className={styles.menuNavigation}>
          {links.map((link) => (
            <li key={link.url} className={styles.menuNavigationItem}>
              <Link
                href={link.url}
                onClick={link.onClick}
                className={cn(styles.menuLink, {
                  [styles.menuLinkSelected]: link.selected,
                })}
              >
                {link.title}
              </Link>
            </li>
          ))}
        </menu>
      ) : (
        <>
          <header className={styles.menuCategoryTitle}>
            <span>{categoryName ?? "Navigation"}</span>
            <IconButton
              tooltip=""
              icon={mobileNavOpened ? <CrossIcon /> : <MenuBurgerIcon />}
              onClick={toggleMobileNav}
              noHoverStyle
            />
          </header>
          {sidebarRequest.value ? (
            <HelpCenterTree
              categories={categories}
              articles={articles}
              baseUrl={baseUrl}
              selected={selectedSlugs}
              search={searchQuery}
            />
          ) : (
            // Skeleton placeholder
            <div className={styles.menuNavigation}>
              <menu className={styles.menuNavigationContent}>
                <li
                  className={cn(
                    styles.menuNavigationItem,
                    styles.menuNavigationCategoryItem
                  )}
                >
                  <button className={styles.menuCategoryButton} type="button">
                    <figure
                      className={cn(
                        styles.menuCategoryButtonIcon,
                        styles.loadingPlaceholder
                      )}
                    ></figure>
                    <span
                      className={styles.loadingPlaceholder}
                      style={{ width: 100 }}
                    />
                  </button>
                </li>
                <li
                  className={cn(
                    styles.menuNavigationItem,
                    styles.menuNavigationCategoryItem
                  )}
                >
                  <button className={styles.menuCategoryButton} type="button">
                    <figure
                      className={cn(
                        styles.menuCategoryButtonIcon,
                        styles.loadingPlaceholder
                      )}
                    ></figure>
                    <span
                      className={styles.loadingPlaceholder}
                      style={{ width: 80 }}
                    />
                  </button>
                </li>
                <li
                  className={cn(
                    styles.menuNavigationItem,
                    styles.menuNavigationCategoryItem
                  )}
                >
                  <button className={styles.menuCategoryButton} type="button">
                    <figure
                      className={cn(
                        styles.menuCategoryButtonIcon,
                        styles.loadingPlaceholder
                      )}
                    ></figure>
                    <span
                      className={styles.loadingPlaceholder}
                      style={{ width: 120 }}
                    />
                  </button>
                </li>
              </menu>
            </div>
          )}
        </>
      )}
      <nav className={styles.sidebarNavigation}>
        {appSettings.links?.map((link) => (
          <Link
            key={link.id}
            href={link.url}
            target="_blank"
            className={styles.sidebarNavigationItem}
          >
            {link.label}
            {link.icon && (
              <big className={styles.sidebarNavigationLinkIcon}>
                {link.icon.match(/https?:\/\//i) ? (
                  <img
                    src={link.icon}
                    alt={link.label}
                    className={styles.sidebarNavigationLinkIconImage}
                  />
                ) : (
                  link.icon
                )}
              </big>
            )}
          </Link>
        ))}
        {appSettings.enableSupportPortal && (
          <Link
            href={getUrlWithPreview(
              `${baseUrl}${isCustomerPortalPage ? "" : customerPortalPath}`
            )}
            className={styles.sidebarNavigationItem}
          >
            {isCustomerPortalPage
              ? t("Help Center", appSettings.language)
              : appSettings.supportPortalTitle}
          </Link>
        )}
      </nav>
    </aside>
  );
}

export type TLayoutSidebarLightLink = {
  title: string;
  url: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  selected?: boolean;
};

type TLayoutSidebarLightProps = {
  appSettings: AppSettings;
  className?: string;
  language?: string;
  hasTitle?: boolean;
  categoryName?: string;
  searchValue?: string;
} & (
  | {
      categories: Section[];
      articles: Article[];
      selectedArticleSlug?: string;
      links?: undefined;
    }
  | {
      categories?: undefined;
      articles?: undefined;
      selectedArticleSlug?: undefined;
      links: TLayoutSidebarLightLink[];
    }
);

function collectParentCategorySlugs(
  categories: Section[],
  parentId: string
): string[] {
  const category = categories.find((c) => c.id === parentId);
  if (!category) return [];
  if (!category.parentId) return [category.slug];
  return [
    category.slug,
    ...collectParentCategorySlugs(categories, category.parentId),
  ];
}

function HelpCenterTree({
  className,
  collapsed,
  categories,
  articles,
  baseUrl,
  parentId = null,
  selected = [],
  search,
  renderTitleWithSearch,
}: {
  className?: string;
  collapsed?: boolean;
  categories: Section[];
  articles: Article[];
  baseUrl: string;
  parentId?: string | null;
  selected?: string[];
  search?: string;
  renderTitleWithSearch?: (title: string, search?: string) => React.ReactNode;
}) {
  const getUrlWithPreview = useUrlWithPreview();
  const [visibleCategories, visibleArticles] = useMemo(
    () => filterTreeData(categories, articles, search),
    [categories, articles, search]
  );

  const nestedCategories = visibleCategories.filter(
    (c) => c.parentId === parentId
  );
  const nestedArticles = visibleArticles.filter(
    (a) => a.categoryId === parentId
  );

  const [openItems, setOpenItems] = useState<string[]>(selected);

  const hasSearchQuery = !!search || !!renderTitleWithSearch;
  useEffect(
    () =>
      hasSearchQuery
        ? setOpenItems(visibleCategories.map((c) => c.slug))
        : setOpenItems(selected),
    [hasSearchQuery, visibleCategories]
  );

  const renderTitleCallback = useCallback(
    (title: string) => {
      if (renderTitleWithSearch) return renderTitleWithSearch(title, search);
      if (!search) return title;
      return renderHelpCenterTreeTitle(title, search);
    },
    [renderTitleWithSearch, search]
  );

  if (!nestedCategories.length && !nestedArticles.length) return null;

  return (
    <div
      className={cn(styles.menuNavigation, className, {
        [styles.menuNavigationCollapsed]: collapsed,
      })}
    >
      <menu className={styles.menuNavigationContent}>
        {nestedArticles.map((article) => (
          <li key={article.slug} className={styles.menuNavigationItem}>
            <Link
              href={getUrlWithPreview(`${baseUrl}/articles/${article.slug}`)}
              className={cn(styles.menuLink, {
                [styles.menuLinkSelected]: selected.includes(article.slug),
              })}
            >
              {renderTitleCallback(article.title)}
            </Link>
          </li>
        ))}
        {nestedCategories.map((category) => {
          const isOpen = openItems.includes(category.slug);

          return (
            <li
              key={category.slug}
              className={cn(
                styles.menuNavigationItem,
                styles.menuNavigationCategoryItem
              )}
            >
              <button
                className={cn(styles.menuCategoryButton, {
                  [styles.menuCategoryButtonSelected]: selected.includes(
                    category.slug
                  ),
                })}
                type="button"
                onClick={() =>
                  setOpenItems((items) =>
                    isOpen
                      ? items.filter((i) => i !== category.slug)
                      : [...items, category.slug]
                  )
                }
              >
                <figure className={styles.menuCategoryButtonIcon}>
                  {category.icon || "📁"}
                </figure>
                {renderTitleCallback(category.title)}
                <ChevronIcon
                  dir={isOpen ? "down" : "right"}
                  className={styles.menuCategoryButtonChevron}
                />
              </button>
              <HelpCenterTree
                collapsed={!isOpen}
                categories={visibleCategories}
                articles={visibleArticles}
                baseUrl={baseUrl}
                parentId={category.id}
                selected={selected}
                renderTitleWithSearch={renderTitleCallback}
              />
            </li>
          );
        })}
      </menu>
    </div>
  );
}

function filterTreeData(
  categories: Section[],
  articles: Article[],
  search?: string
): [Section[], Article[]] {
  if (!search) return [categories, articles];

  const lSearch = search.toLowerCase();

  const visibleCategories = categories.filter((c) =>
    c.title.toLowerCase().includes(lSearch)
  );
  const visibleArticles = articles.filter((a) =>
    a.title.toLowerCase().includes(lSearch)
  );

  visibleCategories.forEach(function displayCategoryContent(category) {
    // These are categories that are matched by search and should display all of its content
    articles.forEach((article) => {
      if (
        article.categoryId === category.id &&
        !visibleArticles.includes(article)
      ) {
        visibleArticles.push(article);
      }
    });

    const nestedCategories = categories.filter(
      (c) => c.parentId === category.id
    );
    nestedCategories.forEach((nestedCategory) => {
      if (visibleCategories.includes(nestedCategory)) return;
      visibleCategories.push(nestedCategory);
      displayCategoryContent(nestedCategory);
    });
  });

  visibleArticles.forEach((article) => {
    const category = categories.find((c) => c.id === article.categoryId);
    if (!category) {
      return console.error(`Category not found for article ${article.id}`);
    }

    if (!visibleCategories.includes(category)) visibleCategories.push(category);
  });

  visibleCategories.forEach(function displayCategoryParents(category) {
    if (!category.parentId) return;

    const parent = categories.find((c) => c.id === category.parentId);
    if (!parent)
      return console.error(`Parent category not found for ${category.id}`);

    if (visibleCategories.includes(parent)) return;

    visibleCategories.push(parent);
    displayCategoryParents(parent);
  });

  return [
    visibleCategories.sort((a, b) => a.order - b.order),
    visibleArticles.sort((a, b) => a.order - b.order),
  ];
}

function renderHelpCenterTreeTitle(title: string, search: string) {
  if (!search) return title;
  const lTitle = title.toLowerCase();
  const lSearch = search.toLowerCase();

  if (!lTitle.includes(lSearch)) return title;

  const parts: React.ReactNode[] = [];
  for (let i = 0; i < lTitle.length; i++) {
    const matched = lTitle.indexOf(lSearch, i);
    if (matched === -1) {
      parts.push(title.substring(i));
      break;
    }

    if (matched !== i) parts.push(title.substring(i, matched));
    parts.push(
      <mark key={matched} className={styles.searchMarker}>
        {title.substring(matched, matched + lSearch.length)}
      </mark>
    );
    i = matched + lSearch.length - 1;
  }

  return <span>{parts}</span>;
}

export const searchHotkey = hotkey({ mac: "cmd+k", win: "ctrl+k" }, "hidden");
